import { clearViewCaches } from './workspaceViewCache'
import { navigate } from 'gatsby-link'
import {
  isIOSPreferSafariBrowser,
  markSafariHandoffRedirectAttempted,
  shouldTrySafariHandoffRedirect,
  toXSafariHttpsUrl,
} from '../lib/iosSetupBrowser'
import { defaultSignedInHomePath } from './doggosRoles'

function setupReturnPath(raw) {
  const value = String(raw || '').trim().split('?')[0]
  if (!value.startsWith('/') || value.startsWith('//')) return null
  const path = value.replace(/\/+$/, '') || '/'
  return path === '/setup' ? '/setup/' : null
}

function decodeJwtPayload(token) {
  try {
    const value = String(token || '').trim()
    if (!value) return null
    const base64Url = value.split('.')[1]
    if (!base64Url) return null
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4)
    return JSON.parse(window.atob(padded))
  } catch {
    return null
  }
}

function decodeJwtUserId(token) {
  const payload = decodeJwtPayload(token)
  return payload?.user_id ? String(payload.user_id) : null
}

/** Prevents onClientEntry + AuthProvider + onRouteUpdate from stacking reloads. */
let magicLinkConsumeInflight = false

/**
 * Apply `?doggos_l=` from the current URL.
 * Invite / magic links must replace any existing session. Always hard-navigate
 * so Stream Chat remounts as the link's user (soft SPA navigate left the
 * previous account connected when an invite was opened while already signed in).
 *
 * Safe to call on every route update; no-ops when the query param is absent.
 * @returns {boolean} true when a magic-link token was consumed
 */
export function consumeMagicLinkFromLocation() {
  if (typeof window === 'undefined') return false
  if (magicLinkConsumeInflight) return true
  const params = new URLSearchParams(window.location.search)
  const token = String(params.get('doggos_l') || '').trim()
  if (!token) return false

  const onSetup =
    window.location.pathname.replace(/\/+$/, '') === '/setup'
  const returnTo = setupReturnPath(params.get('return_to'))

  // Handoff QR often opens in Chrome or an in-app browser; Safari keeps its
  // own storage. One attempt: reopen this URL (including token) in Mobile Safari.
  if (
    (onSetup || returnTo) &&
    isIOSPreferSafariBrowser() &&
    shouldTrySafariHandoffRedirect()
  ) {
    markSafariHandoffRedirectAttempted()
    window.location.replace(toXSafariHttpsUrl(window.location.href))
    return true
  }

  magicLinkConsumeInflight = true
  window.localStorage.setItem('doggos_l', token)
  window.dispatchEvent(
    new CustomEvent('doggos-auth-login', { detail: { token } })
  )

  const home = defaultSignedInHomePath(decodeJwtPayload(token) || {})
  const dest = returnTo || (onSetup ? '/setup/' : home)
  window.location.replace(`${window.location.origin}${dest}`)
  return true
}

/** Clear the CRM login token from this origin. */
export function clearLoginSession() {
  if (typeof window === 'undefined') return
  clearViewCaches()
  window.localStorage.removeItem('doggos_l')
  window.dispatchEvent(new CustomEvent('doggos-auth-logout'))
}

/**
 * Persist a magic-link JWT and notify mounted profile hooks when the account
 * identity changes so nav/sidebar avatars swap immediately.
 *
 * Same-user token refreshes from /api/login update storage only — broadcasting
 * every re-signed JWT remounted Stream Chat on the dashboard.
 * Pass `{ forceNotify: true }` when applying an invite / URL magic link so
 * listeners still refresh claims (role, workspace) for the same user_id.
 */
export function setLoginSession(token, options = {}) {
  if (typeof window === 'undefined') return
  const forceNotify = Boolean(options.forceNotify)
  const value = String(token || '').trim()
  if (!value) {
    clearLoginSession()
    return
  }
  const prev = window.localStorage.getItem('doggos_l')
  const prevUserId = decodeJwtUserId(prev)
  const nextUserId = decodeJwtUserId(value)
  window.localStorage.setItem('doggos_l', value)
  if (forceNotify || prevUserId !== nextUserId) {
    window.dispatchEvent(
      new CustomEvent('doggos-auth-login', { detail: { token: value } })
    )
  }
}

/** One in-flight /api/login refresh shared by all useProfileData instances. */
let loginRefreshInflight = null

/**
 * Re-sign the stored session JWT. Coalesces concurrent callers so the dashboard
 * does not fire N parallel refreshes (one per mounted profile hook).
 * @returns {Promise<string|null>} fresh token, or null if no session
 */
export function refreshLoginSession() {
  if (typeof window === 'undefined') return Promise.resolve(null)
  if (loginRefreshInflight) return loginRefreshInflight

  loginRefreshInflight = (async () => {
    const l = window.localStorage.getItem('doggos_l')
    if (!l) return null

    const response = await fetch('/api/login', {
      method: 'post',
      body: JSON.stringify({ l }),
    })

    if (!response.ok) {
      const err = new Error('login_refresh_failed')
      err.status = response.status
      throw err
    }

    const login = await response.json()
    const token = String(login?.l || '').trim()
    if (!token) {
      const err = new Error('login_refresh_failed')
      err.status = 500
      throw err
    }
    // A refresh started before a workspace switch must not restore its scope.
    if (window.localStorage.getItem('doggos_l') !== l) {
      return window.localStorage.getItem('doggos_l')
    }
    setLoginSession(token)
    return token
  })().finally(() => {
    loginRefreshInflight = null
  })

  return loginRefreshInflight
}

/**
 * End the CRM session and return to the public homepage.
 * Uses a hard navigation so Stream, cached profile state, and JWT refresh
 * do not keep the user signed in (common on Vercel preview / dev URLs).
 */
export function logout() {
  if (typeof window === 'undefined') return
  clearLoginSession()
  window.location.replace(`${window.location.origin}/`)
}

export async function listAvailableAccounts() {
  const token = window.localStorage.getItem('doggos_l')
  const response = await fetch('/api/accounts', {
    headers: { Authorization: `Bearer ${token}` },
  })
  if (!response.ok)
    throw new Error('Could not load accounts. Please try again.')
  return (await response.json()).accounts || []
}

export async function switchWorkspaceAccount(accountId) {
  const token = window.localStorage.getItem('doggos_l')
  if (decodeJwtPayload(token)?.accountId === accountId) return
  const response = await fetch('/api/accounts', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ accountId }),
  })
  if (!response.ok)
    throw new Error('Could not switch accounts. Please try again.')
  const nextToken = (await response.json()).l
  const claims = decodeJwtPayload(nextToken)
  if (
    claims?.accountId !== accountId ||
    claims?.user_id !== decodeJwtUserId(token)
  ) {
    throw new Error('Could not verify the selected account.')
  }
  if (window.localStorage.getItem('doggos_l') !== token) {
    throw new Error('Your session changed. Please try again.')
  }
  // The shell keys its workspace subtree by authenticated scope, discarding
  // old mounted state while profile listeners invalidate workspace caches.
  setLoginSession(nextToken, { forceNotify: true })
  if (!/^\/dashboard(?:\/|$)/.test(window.location.pathname)) {
    await navigate('/dashboard/', { replace: true })
  }
}
